import { useCallback, useEffect, useState } from "react"

import { AuthQueryKeys } from "@/configs/common"
import { SnsUserStatus } from "@/types/user"
import { useConnection } from "wagmi"
import { useQueryClient } from "@tanstack/react-query"

interface UseProfileManagementProps {
  user?: any
  address?: `0x${string}`
  profileError?: Error | null
  refetchProfile: () => Promise<any>
  disconnectWallet: () => Promise<void>
  setIsVerified: (verified: boolean) => void
}

export const useProfileManagement = ({
  user,
  address,
  profileError,
  refetchProfile,
  disconnectWallet,
  setIsVerified,
}: UseProfileManagementProps) => {
  const [showInactiveModal, setShowInactiveModal] = useState<boolean>(false)
  const { isConnected } = useConnection()
  const queryClient = useQueryClient()

  // Close modal when disconnected
  useEffect(() => {
    if (!isConnected) {
      setShowInactiveModal(false)
    }
  }, [isConnected])

  // Check user status and show modal immediately when 404 or inactive
  // Show modal as soon as error is detected, don't wait for all retries
  useEffect(() => {
    // Only check if:
    // 1. Wallet is connected
    // 2. We have an address
    if (!isConnected || !address) {
      return
    }

    // Check if profile fetch failed (404 or other error)
    // Show modal immediately when error is detected, even on first error
    // Don't wait for all retries to complete
    if (profileError) {
      const error = profileError as any
      const is404 = error?.response?.status === 404

      if (is404) {
        // User doesn't exist - show modal immediately on first 404
        // Retries will continue in background but user can see the issue
        setShowInactiveModal(true)
        // Reset verification state to prevent button from being stuck
        setIsVerified(false)
      } else {
        // Other error - also show modal
        setShowInactiveModal(true)
        setIsVerified(false)
      }
    } else if (user && user.status !== SnsUserStatus.Active) {
      // User exists but is not active - show modal immediately
      setShowInactiveModal(true)
      setIsVerified(false)
    } else if (user && user.status === SnsUserStatus.Active) {
      // User is active - close modal if it was open
      setShowInactiveModal(false)
    }
  }, [user, address, isConnected, profileError, setIsVerified])

  const handleRefetchProfile = useCallback(async () => {
    if (address) {
      await queryClient.invalidateQueries({
        queryKey: [AuthQueryKeys.AVACUS_PROFILE, address],
      })
      await refetchProfile()
    }
  }, [address, queryClient, refetchProfile])

  const handleDisconnect = useCallback(async () => {
    await disconnectWallet()
    setShowInactiveModal(false)
  }, [disconnectWallet])

  return {
    showInactiveModal,
    setShowInactiveModal,
    handleRefetchProfile,
    handleDisconnect,
  }
}
